"use client";

import Image from "@/components/CwfImage";
import { createPortal } from "react-dom";
import { useEffect } from "react";
import { playUiCelebrate, playUiTap } from "@/lib/audio/play";
import { eventRewardDef } from "@/lib/events/eventProgressRewards";
import { eventMenuIconSrc } from "@/lib/events/eventArt";
import type { PendingEventIconFly } from "@/lib/eventIconFlyToMenu";
import { CurrencyIcon } from "@/components/ui/CurrencyIcons";

function humanizeEventKey(key: string): string {
  return key
    .trim()
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

export function EventTierRewardModal({
  completions,
  totalCoinsQueued,
  onContinue,
}: {
  completions: PendingEventIconFly[];
  /** Total HUD coin burst (may include duel payout combined in session queue). */
  totalCoinsQueued: number;
  onContinue: () => void;
}) {
  useEffect(() => {
    try {
      playUiCelebrate();
    } catch {
      /* noop */
    }
  }, []);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") {
        try {
          playUiTap();
        } catch {
          /* noop */
        }
        onContinue();
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onContinue]);

  if (typeof document === "undefined") return null;

  const milestoneSum = completions.reduce((s, x) => s + x.bonusCoins, 0);
  const showExtraCoins = totalCoinsQueued > milestoneSum;
  const extras = Math.max(0, totalCoinsQueued - milestoneSum);

  function handleDismiss() {
    try {
      playUiTap();
    } catch {
      /* noop */
    }
    onContinue();
  }

  return createPortal(
    <div
      className="pointer-events-none fixed inset-0 z-[238] flex min-h-0 items-center justify-center overflow-y-auto overscroll-contain px-4 py-6 pb-[max(1.5rem,env(safe-area-inset-bottom))] pt-[max(1rem,env(safe-area-inset-top))] sm:px-5 sm:py-8"
      role="dialog"
      aria-modal="true"
      aria-labelledby="cwf-tier-reward-title"
    >
      <div
        className="pointer-events-none absolute inset-0 bg-[#0a1020]/55 backdrop-blur-[2px]"
        aria-hidden
      />
      <div className="pointer-events-auto relative z-[1] w-full max-w-sm max-h-[min(92dvh,920px)] overflow-y-auto rounded-2xl border-[3px] border-amber-800/95 bg-gradient-to-b from-[#fff7dc] via-amber-200/95 to-amber-500/98 p-[1px] shadow-[0_20px_50px_rgba(0,0,0,0.38)]">
        <div className="rounded-[calc(1rem-2px)] border border-white/65 bg-gradient-to-b from-[#fffbeb] via-amber-100/98 to-amber-200/90 px-4 pb-5 pt-4 sm:px-5">
          <p
            id="cwf-tier-reward-title"
            className="cwf-tier-reward-title-pop text-center font-display text-[clamp(1.75rem,8vw,2.85rem)] font-black uppercase italic leading-none tracking-tight text-amber-200"
            style={{
              textShadow:
                "0 2px 0 #7c2d12, 0 4px 0 #431407, 0 7px 20px rgba(0,0,0,0.5), 0 0 36px rgba(251,191,46,0.55)",
              WebkitTextStroke: "1px rgba(67,20,7,0.95)",
              paintOrder: "stroke fill",
            }}
          >
            Rewards!!
          </p>
          <p className="mt-2 text-center font-display text-xs font-black uppercase tracking-wide text-[#5c3018]/88 sm:text-[0.8125rem]">
            Event tier complete · take your haul home
          </p>

          <div className="mt-4 rounded-xl border border-amber-900/45 bg-[#f9ebcf]/75 p-3 shadow-[inset_0_2px_6px_rgba(0,0,0,0.06)] sm:p-3.5">
            <ul className="space-y-2.5">
              {completions.map((c, i) => {
                const def = eventRewardDef(c.eventKey);
                const iconSrc =
                  eventMenuIconSrc(c.eventKey) ?? "/events-tab-icon.webp";
                return (
                  <li
                    key={`${c.eventKey}-${i}`}
                    className="flex items-center gap-3 rounded-xl border border-amber-800/55 bg-[#fff8ea]/95 px-2.5 py-2 shadow-[inset_0_1px_0_rgba(255,255,255,0.65)] sm:px-3 sm:py-2.5"
                  >
                    <div className="relative h-[2.875rem] w-[2.875rem] shrink-0 overflow-hidden rounded-xl border-[2px] border-amber-800/65 bg-[#fde9c9] shadow-[0_4px_10px_rgba(0,0,0,0.18)]">
                      <Image
                        src={iconSrc}
                        alt=""
                        fill
                        sizes="46px"
                        className="object-cover"
                      />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="line-clamp-2 break-words font-display text-[0.7rem] font-black uppercase leading-tight tracking-wide text-[#5c3018]/95">
                        {def.rewardLabel}
                      </p>
                      <p className="mt-0.5 line-clamp-2 break-words font-display text-[0.625rem] font-semibold uppercase leading-tight text-[#7b422f]/75">
                        {humanizeEventKey(c.eventKey)}
                      </p>
                    </div>
                    <span className="flex shrink-0 items-center gap-1 rounded-full border-[2px] border-amber-800/65 bg-gradient-to-b from-amber-100 via-amber-300 to-amber-500 px-2.5 py-1 font-display text-[0.68rem] font-black tabular-nums uppercase text-amber-950 shadow-[0_3px_10px_rgba(0,0,0,0.28)]">
                      <CurrencyIcon kind="coin" size={14} className="h-3 w-3" />+
                      {c.bonusCoins}
                    </span>
                  </li>
                );
              })}
            </ul>

            {showExtraCoins ? (
              <div className="mt-3 flex items-center gap-3 rounded-xl border border-emerald-900/42 bg-emerald-50/95 px-2.5 py-2 shadow-[inset_0_1px_0_rgba(255,255,255,0.65)] sm:px-3 sm:py-2.5">
                <div className="relative h-[2.875rem] w-[2.875rem] shrink-0 overflow-hidden rounded-xl border-[2px] border-emerald-800/55 bg-emerald-100/90 shadow-[0_4px_10px_rgba(22,101,52,0.2)]">
                  <Image
                    src="/ui/icons/coin.webp"
                    alt=""
                    fill
                    sizes="46px"
                    className="object-contain p-1.5 drop-shadow-[0_2px_4px_rgba(0,0,0,0.25)]"
                  />
                </div>
                <p className="min-w-0 flex-1 font-display text-[0.6875rem] font-black uppercase leading-snug text-emerald-950/90">
                  Match &amp; other coins
                </p>
                <span className="flex shrink-0 items-center gap-1 font-display text-[0.7rem] font-black tabular-nums text-emerald-950">
                  <CurrencyIcon kind="coin" size={14} className="h-3 w-3" />+
                  {extras}
                </span>
              </div>
            ) : null}
          </div>

          <div className="mt-4 flex items-center justify-center gap-2 rounded-xl border-[2px] border-amber-900/65 bg-[#fde68a]/90 px-3 py-3 font-display text-sm font-black tabular-nums uppercase text-[#431407] shadow-[inset_0_2px_0_rgba(255,255,255,0.5)]">
            <CurrencyIcon kind="coin" size={24} className="h-6 w-6" />
            Total +{Math.max(0, totalCoinsQueued)}
          </div>

          <button
            type="button"
            onClick={handleDismiss}
            className="mt-5 w-full rounded-xl border-[3px] border-[#1d4e9e] bg-gradient-to-b from-[#d4f0ff] via-[#5eb5ff] to-[#1e84eb] py-3.5 font-display text-sm font-black uppercase tracking-wide text-white shadow-[inset_0_3px_0_rgba(255,255,255,0.5),inset_0_-3px_0_rgba(12,53,122,0.55),0_4px_0_#153f94,0_8px_20px_rgba(12,61,138,0.45)] transition hover:brightness-105 active:translate-y-[2px]"
          >
            Collect
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
